import styled from "@emotion/styled";
import {
  Box,
  Button,
  Card,
  CardContent,
  DialogContent,
  Divider,
  Grid,
  Rating,
  Skeleton,
  Typography,
} from "@mui/material";
import ProductServices from "src/services/product";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CartServices from "src/services/cart";
import { AmazonIcon } from "src/icons/reciept";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { useState } from "react";
import {convertUrlToMozonoUrl, currencyFormat, transformAmazonUrl} from "src/utility/Utils";
import Dialog from "@mui/material/Dialog";
import AmazonLoading from "src/components/AmazonLoading";
import { useAuth } from "src/hooks/useAuth";
import {
  ProductError,
  useProductStore,
  PriceInfo,
  PriceInfoLoading,
} from "src/features/product";

import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

const ContextContainer = styled("div")(({ theme }) => ({
  "#ez-toc-container": {
    ".ez-toc-title-container": {
      display: "none",
    },
    nav: {
      "ul li": {
        listStyle: "none",
      },
    },
  },
}));

const ProductThumnail = styled("img")(({ theme }) => ({
  width: "100%",
  height: "100%",
  objectFit: "contain",
}));

const FixedBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    position: "fixed",
    bottom: theme.spacing(theme.mixins.toolbar.minHeight / 5),
    left: 0,
    right: 0,
    backgroundColor: "white",
    padding: 10,
    borderTop: "1px solid #efefef",
  },
}));

const PaymentCard = styled(Card)(({ theme }) => ({
  "& .__amazonlink": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const ProductPage = (props) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { isLogin } = useAuth();
  const productPrice = useProductStore((state) => state.productPrice);
  const setProductPrice = useProductStore((state) => state.setProductPrice);

  const {
    code,
    domain = "amazon.ae",
    url: productUrl,
    productasin = 0,
  } = router.query;
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["product", code],
    queryFn: () => ProductServices.fetchProduct(code, domain, productUrl),
    enabled: !!code && !!productUrl,
    staleTime: 0,
    cacheTime: 0,
  });

  let product;
  if (data?.status === "success") {
    // console.log(data);

    product = data.data;
  }

  const { mutate: addToCartMutate, isLoading: addToCartLoading } = useMutation(
    CartServices.createCartProduct,
    {
      onSuccess: (data) => {
        queryClient.setQueryData(["cart"], data);
        router.push("/checkout");
      },
      onError: (err) => {},
      onSettled: () => {},
    }
  );

  const [activeImage, setActiveImage] = useState(0);

  const [selectedVariants, setSelectedVariants] = useState({});

  const handleSelect = (variantName, itemName) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [variantName]: itemName,
    }));
  };

  const handleAddToCartClick = (price, deliveryType) => {
    if (!isLogin) {
      router.push(
        `/login?redirect=/product/dp/${router.query.code}&url=${productUrl}`
      );
      return;
    }
    let selectedVariations = "";
    if (product.variations && product.variations.length > 0) {
      product.variations.forEach((variant) => {
        selectedVariations = `${selectedVariations} ${variant.name || ""}:${
          variant.isSelected || ""
        }\n`;
      });
    }

    addToCartMutate({
      ...product,
      price,
      deliveryType,
      description: selectedVariations,
      product_description: selectedVariations,
      images: [{large: product.images[0]}],
    });
  };

  const productIsAvailable = Boolean(product?.availability);

  return (
    <>
      <Head>
        <title>{product?.fa_title || product?.title}</title>

        <meta
          name="description"
          content={product?.abouts?.[0] || product?.abouts?.[1]}
        />
      </Head>
      <Box
        sx={{
          flexGrow: 1,
          position: "relative",
          pt: { xs: 3, lg: 0 },
          mb: 20,
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{ mt: { xs: 2, lg: 0 }, px: { xs: 3, lg: 0 } }}
        >
          <Grid item xs={12} lg={9}>
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems={"flex-start"}>
                  <Grid
                    item
                    xs={12}
                    lg={5}
                    display={"flex"}
                    alignItems={"center"}
                  >
                    <Box display={"flex"} flexDirection={"column"}>
                      {product?.images &&
                        product?.images.slice(0, 6).map(
                          (image, i) =>
                            image && (
                              <Box
                                key={i}
                                component={"div"}
                                onClick={() => setActiveImage(i)}
                                sx={{
                                  border: 1,
                                  borderColor:
                                    i === activeImage ? "primary.main" : "gray",
                                  borderRadius: 0.5,
                                  p: 0.5,
                                  m: 0.5,
                                  width: 50,
                                  height: 50,
                                  cursor: "pointer",
                                }}
                              >
                                <ProductThumnail
                                  src={image}
                                  alt={image}
                                />
                              </Box>
                            )
                        )}
                    </Box>
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      {isLoading ? (
                        <Skeleton width={300} height={350} sx={{ flex: 0.8 }} />
                      ) : (
                        product?.images &&
                        product?.images[activeImage] && (
                          <Image
                            width={250}
                            height={180}
                            style={{ objectFit: "contain" }}
                            src={product.images[activeImage]}
                            alt={product.title}
                            unoptimized={true}
                          />
                        )
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={7}>
                    <Box sx={{ direction: "rtl" }}>
                      <Typography variant="h6" dir="rtl" textAlign={"end"}>
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          product?.fa_title || product?.title
                        )}
                      </Typography>
                      {isLoading && (
                        <Typography
                          variant="h5"
                          sx={{ direction: "ltr" }}
                          textAlign={"end"}
                          my={2}
                          color={"primary"}
                        >
                          <Skeleton />
                        </Typography>
                      )}
                      <Box
                        mt={2}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: { xs: "center", lg: "start" },
                        }}
                      >
                        {product?.rating &&
                          Object.keys(product?.rating)?.length > 0 &&
                          Object.keys(product?.rating)?.map(
                            (key, i) =>
                              key === "totalRates" && (
                                <Typography
                                  variant="subtitle2"
                                  // sx={{ direction: "rtl" }}
                                >
                                  {`(${product?.rating[key]})`}
                                </Typography>
                              )
                          )}
                        {product?.rating &&
                          Object.keys(product?.rating)?.length > 0 &&
                          Object.keys(product?.rating)?.map(
                            (key, i) =>
                              key === "rate" && (
                                <>
                                  <Box sx={{}}>
                                    <Rating
                                      size="small"
                                      name="half-rating-read"
                                      sx={{ mx: 1, direction: "ltr" }}
                                      precision={0.1}
                                      value={+product?.rating[key]}
                                      readOnly
                                    />
                                  </Box>

                                  <Typography variant="subtitle2">
                                    : امتیاز
                                  </Typography>
                                </>
                              )
                          )}
                      </Box>

                      {product?.price?.dirham_price > 0 && (
                        <Typography
                          variant="h5"
                          sx={{ direction: "ltr" }}
                          textAlign={"end"}
                          my={2}
                          color={"primary"}
                        >
                          {isLoading ? <Skeleton /> : <></>}
                          {product.price.price_type === "range"
                            ? `${currencyFormat(
                                product.price.end_toman_price
                              )} - ${currencyFormat(
                                product.price.start_toman_price
                              )}`
                            : productPrice > 0 && currencyFormat(productPrice)}
                          {productPrice > 0 && " تومان"}
                        </Typography>
                      )}

                      {isSuccess &&
                      product?.variations?.some(
                        (v) =>
                          v.selected?.toLowerCase()?.trim() === "select" ||
                          v.selected?.toLowerCase()?.trim() === "اختر"
                      ) || (productIsAvailable && !product?.price?.dirham_price) ? (
                        <Typography
                          variant="h6"
                          textAlign={"center"}
                          my={2}
                          color="primary.main"
                          dir="rtl"
                        >
                          برای افزودن به سبد خرید نوع محصول خود را انتخاب کنید
                        </Typography>
                      ) : (
                        !isLoading &&
                        isSuccess &&
                        !product?.price?.dirham_price && (
                          <Typography
                            variant="h5"
                            textAlign={"center"}
                            my={2}
                            color="primary.main"
                            dir="rtl"
                          >
                            محصول موجود نمی باشد
                          </Typography>
                        )
                      )}


                      {product?.description ? (
                          <ContextContainer
                              className="ql-editor"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(product.description?.main.length ? product.description?.main : product.description?.subDescription?.join('\n')),
                              }}
                          ></ContextContainer>
                      ) : (
                          <Typography variant="caption">
                            {" "}
                            {isLoading ? (
                                <Skeleton />
                            ) : (
                                product?.abouts[0] || product?.abouts[1]
                            )}{" "}
                          </Typography>
                      )}

                      <Divider sx={{ my: 3 }} />

                      {product?.variations?.map((variation) => (
                          <Box key={variation.name}>
                            <Typography variant="subtitle2" sx={{ "& span": { color: "black" } }}>
                              {variation.name}
                            </Typography>
                            <Box mt={2}>
                              {variation.items.map((item) => {
                                const isColorVariant = variation.name.toLowerCase() === "color";
                                const isSelected = item?.isSelected === true;

                                const buttonVariant = isColorVariant
                                    ? "outlined"
                                    : isSelected
                                        ? "contained"
                                        : "outlined";

                                const buttonSx = isColorVariant
                                    ? {
                                      ml: 2,
                                      border: "1px solid",
                                      borderColor: isSelected ? "primary.main" : "secondary.main",
                                      "&.active": {},
                                    }
                                    : {
                                      ml: 2,
                                      mb: 2,
                                    };

                                return (
                                    <Button
                                        key={item.name}
                                        size="small"
                                        href={`${transformAmazonUrl(item.link)}?url=${encodeURIComponent(item.link)}`}
                                        LinkComponent={Link}
                                        variant={buttonVariant}
                                        sx={buttonSx}
                                        onClick={() => handleSelect(variation.name, item.name)}
                                    >
                                      {isColorVariant ? (
                                          <Image
                                              width={35}
                                              height={35}
                                              src={item.name}
                                              alt={`${variation.name} variant`}
                                              unoptimized
                                          />
                                      ) : (
                                          item.name
                                      )}
                                    </Button>
                                );
                              })}
                            </Box>
                            <Divider sx={{ my: 3 }} />
                          </Box>
                      ))}

                      {isLoading &&
                        [...Array(3)].map((_, i) => (
                          <>
                            <Skeleton height={20} />
                            <Skeleton height={70} />

                            <Divider sx={{ my: 3 }} />
                          </>
                        ))}

                      <Box>
                        {Array.isArray(product?.details) &&
                            product.details.map((item, i) => (
                                <Typography
                                    mb={1}
                                    key={i}
                                    variant="subtitle2"
                                    sx={{ "& span": { color: "black" } }}
                                >
                                  <span>{item.key}</span>: {item.value}
                                </Typography>
                            ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={3}>
            <PaymentCard>
              <CardContent>
                <a
                  href={product?.url}
                  target="_blank"
                  className="__amazonlink"
                  css={{ display: "flex" }}
                  rel="noreferrer"
                >
                  <>
                    <Typography color="primary.main">
                      مشاهده در آمازون
                    </Typography>
                    <AmazonIcon sx={{ width: 35, height: 35, ml: 2 }} />
                  </>
                </a>
                {isLoading ? (
                  <PriceInfoLoading />
                ) :
                    productIsAvailable && !product?.price?.dirham_price
                    ? <Typography
                            textAlign={"center"}
                            mt={10}
                            color="primary.main"
                            dir="rtl"
                        >
                            برای دریافت قیمت محصول ابتدا یکی از مدل‌های آن را انتخاب کنید.
                        </Typography>
                   : (
                  product?.price && (
                    <PriceInfo
                      product={{
                        id: productasin,
                        url: product.code,
                        price: product.price,
                      }}
                      deliveryPrice={
                        Number(product?.extra_items?.deliveryPrice) || 0
                      }
                      productImage={product?.images[0]}
                      productTitle={product?.title}
                      isProductLoading={isLoading}
                      onAddtoCartClick={handleAddToCartClick}
                      addToCartLoading={addToCartLoading}
                      uaeDeliveryPrice={
                        product?.extra_items?.deliveryPrice || 0
                      }
                    />
                  )
                )}
              </CardContent>
            </PaymentCard>
          </Grid>
        </Grid>
      </Box>
      {/* <GeneralProductInfo /> */}
      {/* <Backdrop open /> */}
      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Box>
            <AmazonLoading />
          </Box>
          <Typography variant="h5" mt={10} mb={3}>
            در حال دریافت محصول از آمازون ...
          </Typography>
        </DialogContent>
      </Dialog>
      <ProductError open={!isLoading && !product} />
    </>
  );
};

export default ProductPage;
